<script setup lang="ts">
import EstateAddress from '../shared/EstateAddress.vue'
import EstateFavoriteTriggerVue from '../shared/EstateFavoriteTrigger.vue'
import BackButton from '~/components/global/BackButton.vue'
import useArrangeVisitModalStore from '~/stores/modals/useArrangeVisitModal'
import Icon from '~/components/global/Icon.vue'
import type { EstateDetailInterface } from '~/types/api/EstateDetail'

const props = defineProps<{
  estateData: EstateDetailInterface
}>()

const arrangeVisitModalStore = useArrangeVisitModalStore()
const { t } = useI18n()
const router = useRouter()

const title = computed(() =>
  t('pages.estateDetail.header.title', {
    _0: props.estateData.name,
    _1: t(`pages.estateDetail.header.estateType.${props.estateData.type}`),
  })
)

const lastRoutePath = computed(() => {
  let back = router.options.history.state.back

  if (back) {
    back = back.toString().split('?')[0].split('#')[0]
  }

  return back
})

const listListingLink = getEstateListingLink({
  estateType: props.estateData.type,
  listingType: 'list',
})

const mapListingLink = getEstateListingLink({
  estateType: props.estateData.type,
  listingType: 'map',
})

const backText = computed(() => {
  if (lastRoutePath.value === mapListingLink?.path) {
    return t(`pages.estateDetail.header.back.map.long.${props.estateData.type}`)
  }

  if (lastRoutePath.value === listListingLink?.path) {
    return t(
      `pages.estateDetail.header.back.list.long.${props.estateData.type}`
    )
  }

  return null
})

const backTextShort = computed(() => {
  if (lastRoutePath.value === mapListingLink?.path) {
    return t(`pages.estateDetail.header.back.map.short`)
  }

  if (lastRoutePath.value === listListingLink?.path) {
    return t(`pages.estateDetail.header.back.list.short`)
  }

  return null
})

const mapLink = computed(() => {
  if (!props.estateData.latitude || !props.estateData.longitude) {
    return null
  }

  return getEstateListingLink({
    estateType: props.estateData.type,
    listingType: 'map',
    query: {
      map: encodeURIComponent(
        JSON.stringify({
          center: {
            lat: props.estateData.latitude,
            lng: props.estateData.longitude,
          },
          zoom: 18,
          popupId: props.estateData.id,
        })
      ),
    },
  })
})

async function shareProduct() {
  const shareData = {
    title: window.document.title,
    url: window.location.href,
  }

  try {
    await navigator.share(shareData)
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      return
    }

    try {
      // copy url to clipboard if share is not supported
      await navigator.clipboard.writeText(shareData.url)

      showToast({
        title: t('pages.estateDetail.header.share.copiedToClipboard'),
      })
    } catch (err) {
      showToast({
        title: t('pages.estateDetail.header.share.error'),
        color: 'danger',
      })
    }
  }
}
</script>

<template>
  <div class="c-estate-detail-header">
    <div class="c-estate-detail-header__back">
      <BackButton :back-text="backText" :back-text-short="backTextShort" />
    </div>

    <h1 class="c-estate-detail-header__title">{{ title }}</h1>

    <div class="c-estate-detail-header__info">
      <div class="sc-address">
        <EstateAddress :estate-data="estateData" />
      </div>

      <div v-if="mapLink" class="sc-map-link">
        <NuxtLink :href="mapLink" class="c-link c-link--underlined">
          {{ $t('pages.estateDetail.header.showOnMap') }}
        </NuxtLink>
      </div>
    </div>

    <div class="c-estate-detail-header__actions">
      <IButton color="secondary" @click="arrangeVisitModalStore.open()">{{
        $t('pages.estateDetail.header.arrangeVisit')
      }}</IButton>

      <EstateFavoriteTriggerVue
        :estate-data="estateData"
        :detail-header="true"
      />

      <!-- TODO: print -->
      <!-- <IButton class="is-icon-button _lg:visible" color="primary">
        <Icon name="print" />
      </IButton> -->

      <IButton class="is-icon-button" color="primary" @click="shareProduct">
        <Icon name="share" />
      </IButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-detail-header {
  padding-top: 42px;
  display: grid;
  grid-template:
    'back back' auto
    'title actions' auto
    'info actions' auto
    / 1fr auto;
  gap: 0 24px;

  @include breakpoint-down('lg') {
    padding-top: 32px;
  }

  @include breakpoint-down('md') {
    grid-template:
      'back' auto
      'title' auto
      'info' auto
      'actions' auto
      / auto;
  }

  &__back {
    grid-area: back;
    margin-bottom: 42px;
    justify-self: start;

    @include breakpoint-down('lg') {
      margin-bottom: 32px;
    }
  }

  &__title {
    grid-area: title;
    font-size: 58px;
    line-height: 0.96;
    margin-bottom: 0;

    @include breakpoint-down('lg') {
      font-size: 48px;
    }
    @include breakpoint-down('sm') {
      font-size: 34px;
    }
  }

  &__info {
    grid-area: info;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 16px;

    @include breakpoint-down('lg') {
      margin-bottom: 32px;
    }

    .sc-address {
      font-size: 22px;

      @include breakpoint-down('lg') {
        font-size: 18px;
      }

      @include breakpoint-down('md') {
        font-size: 17px;
      }
    }

    .c-link {
      @include breakpoint-down('md') {
        font-size: 15px;
      }
    }
  }

  &__actions {
    grid-area: actions;
    display: flex;
    align-self: start;
    gap: 4px;

    :deep(.button) {
      @include breakpoint-down('md') {
        --button--font-size: 14px;
        --button--padding-top: 8px;
        --button--padding-bottom: 8px;
        --button--padding-left: 16px;
        --button--padding-right: 16px;

        min-height: 36px;
      }

      &.is-icon-button,
      &.c-estate-favorite-trigger__button {
        --button--font-size: 22px;
        --button--padding-left: 14px;
        --button--padding-right: 14px;

        min-width: 54px;

        @include breakpoint-down('md') {
          --button--font-size: 18px;
          --button--padding-top: 2px;
          --button--padding-bottom: 2px;
          --button--padding-left: 9px;
          --button--padding-right: 9px;

          min-width: 36px;
        }
      }

      &.c-estate-favorite-trigger__button {
        --button--font-size: 16px;

        @include breakpoint-down('md') {
          --button--font-size: 14px;
        }
      }
    }
  }
}
</style>
